import { NativeDesktopWindow } from "./NativeDesktopWindow";
import LoggerService from "@bfx/logger";
const logger = LoggerService.for(__dirname);

/**
 * Simple class that monitors the class list of the window looking for changes in the
 * focus/blur state of the window. It handles the registration and removal of correct listeners for
 * activation events, and callbacks are notified with simple boolean values. KISS for the moment.
 *
 * Instance of class is created with:
 * @example
 * let monitor = new ActiveWindowMonitor( myWindow);
 * monitor.start(  callback);
 * ...
 * monitor.stop()
 */
export class ActiveWindowMonitor {
  private windowActive: boolean;

  private nativeWindow: Window;

  /** local listener instances for unsubscription */
  private focusListener: () => void;
  private blurListener: () => void;

  activeCallback: (value: boolean) => void;

  constructor(private nativeDesktopWindow: NativeDesktopWindow) {
    if (nativeDesktopWindow) {
      this.nativeWindow = nativeDesktopWindow.getNativeWindow();
      if (this.nativeWindow) {
        this.windowActive = this.nativeWindow.document?.hasFocus();
      } else {
        logger.out.error("awm - no window assocaited with native window instance");
      }

      this.init();
    }
  }

  private init() {}

  /**
   * Start monitoring the Active state of the window
   * @param activeCallback
   */
  public start(activeCallback: (value: boolean) => void) {
    this.activeCallback = activeCallback;

    this.focusListener = this.focusHandler.bind(this);
    this.blurListener = this.blurHandler.bind(this);

    if (this.nativeWindow) {
      this.nativeWindow.addEventListener("focus", this.focusListener);
      this.nativeWindow.addEventListener("blur", this.blurListener);
    }
  }

  private focusHandler() {
    // logger.out.info("awm - WinEvent:Window is focused");
    this.updateActive(true);
  }

  private blurHandler() {
    // logger.out.info("awm - WinEvent:Window is blurred");
    this.updateActive(false);
  }

  /**
   * Stop monitoring thr active state. Must be called before removing the window
   */
  stop() {
    if (this.nativeWindow) {
      this.nativeWindow.removeEventListener("focus", this.focusListener);
      this.nativeWindow.removeEventListener("blur", this.blurListener);
    }
  }

  private updateActive(isActive: boolean) {
    if (this.windowActive !== isActive) {
      this.windowActive = isActive;
      // logger.out.info(`awm -focused state = ${isActive}`);
      if (this.activeCallback) {
        this.activeCallback(this.windowActive);
      }
    }
  }

  /**
   * Is the window currently active?
   */
  public get isActive(): boolean {
    return this.windowActive;
  }
}
